// Helpers puros da busca de eventos (M5b) — consumidos por GlobalActivityModal.
// Espelham o contrato real do backend (backend/src/services/eventQuery.js,
// backend/src/routes/events.routes.js): nenhuma regra de visibilidade é
// decidida aqui, só formatação de query string, janela de período e leitura
// da linha de status do CSV.

import { parseDate } from './formatRelative';

// Espelha MAX_ORG_WINDOW_DAYS (eventQuery.js) — busca textual e facetas em
// scope=org exigem janela fechada de no máximo este tanto.
export const MAX_ORG_WINDOW_DAYS = 90;
const MAX_ORG_WINDOW_MS = MAX_ORG_WINDOW_DAYS * 24 * 60 * 60 * 1000;

export const PERIOD_PRESETS = [
  { key: '7d', label: '7 dias', days: 7 },
  { key: '30d', label: '30 dias', days: 30 },
  { key: '90d', label: '90 dias', days: 90 },
];

// Preset padrão ao abrir a tela — bem dentro do teto de 90 dias (regra 5).
export const DEFAULT_PERIOD_PRESET = '30d';

/**
 * Resolve o preset/intervalo custom escolhido num par { from, to } pronto
 * para a query string, OU um erro em português pronto para exibir — nunca
 * os dois. `to` ausente = "até agora" (mesma leitura do backend:
 * assertOrgWindow trata fim ausente como Date.now()).
 */
export function resolvePeriod(preset, customFrom, customTo) {
  if (preset !== 'custom') {
    const days = PERIOD_PRESETS.find((p) => p.key === preset)?.days ?? 30;
    return { from: new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString(), to: undefined, error: null };
  }
  if (!customFrom) return { from: undefined, to: undefined, error: 'Informe a data inicial.' };

  const fromDate = new Date(`${customFrom}T00:00:00Z`);
  const toDate = customTo ? new Date(`${customTo}T23:59:59Z`) : new Date();
  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
    return { from: undefined, to: undefined, error: 'Data inválida.' };
  }
  if (fromDate.getTime() > toDate.getTime()) {
    return { from: undefined, to: undefined, error: '"De" precisa ser antes de "até".' };
  }
  if (toDate.getTime() - fromDate.getTime() > MAX_ORG_WINDOW_MS) {
    return { from: undefined, to: undefined, error: `Período máximo de ${MAX_ORG_WINDOW_DAYS} dias — reduza o intervalo.` };
  }
  return { from: fromDate.toISOString(), to: customTo ? toDate.toISOString() : undefined, error: null };
}

/** Query string comum a /events, /events/facets e /events/export.csv. */
export function buildEventsParams({ q, from, to, action, actor, board } = {}, extra = {}) {
  const params = new URLSearchParams();
  params.set('scope', 'org');
  if (q) params.set('q', q);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (action) params.set('action', action);
  if (actor) params.set('actor', String(actor));
  if (board) params.set('board', String(board));
  for (const [k, v] of Object.entries(extra)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  return params;
}

// --- agrupamento de linhas -------------------------------------------------

const GROUP_WINDOW_MS = 5 * 60 * 1000;

/**
 * Agrupa a lista (já ordenada occurred_at DESC, id DESC, como o backend
 * devolve) em linhas de exibição:
 *  - eventos com o MESMO batch_id viram um grupo "batch", em qualquer
 *    posição em que apareçam (mesmo lote cortado entre duas páginas de
 *    "carregar mais" ainda junta certo, porque o índice é por batch_id, não
 *    só pelo vizinho anterior);
 *  - sem lote, mantém o agrupamento que já existia (BoardActivityModal):
 *    mesmo ator + mesma ação + janela de 5 minutos, só olhando o grupo
 *    IMEDIATAMENTE anterior (contíguo).
 */
export function groupEvents(events) {
  const out = [];
  const batchIndex = new Map();

  for (const e of events) {
    if (e.batch_id) {
      const existing = batchIndex.get(e.batch_id);
      if (existing) {
        existing.events.push(e);
        continue;
      }
      const group = {
        kind: 'batch',
        key: `batch:${e.batch_id}`,
        batch_id: e.batch_id,
        actor_user_id: e.actor_user_id,
        actor_name: e.actor_name,
        actor_avatar_url: e.actor_avatar_url,
        action: e.action,
        occurred_at: e.occurred_at,
        time_quality: e.time_quality,
        events: [e],
      };
      batchIndex.set(e.batch_id, group);
      out.push(group);
      continue;
    }

    const last = out[out.length - 1];
    if (
      last && last.kind === 'single'
      && last.actor_user_id === e.actor_user_id
      && last.action === e.action
      && Math.abs((parseDate(last.occurred_at)?.getTime() ?? 0) - (parseDate(e.occurred_at)?.getTime() ?? 0)) <= GROUP_WINDOW_MS
    ) {
      last.events.push(e);
      last.occurred_at = e.occurred_at;
    } else {
      out.push({
        kind: 'single',
        key: `single:${e.id}`,
        actor_user_id: e.actor_user_id,
        actor_name: e.actor_name,
        actor_avatar_url: e.actor_avatar_url,
        action: e.action,
        occurred_at: e.occurred_at,
        time_quality: e.time_quality,
        events: [e],
      });
    }
  }
  return out;
}

/** Rótulo do grupo de lote ("moveu 40 cartões") por ação — fallback genérico
 * para ação que ainda não grava lote hoje (services/requestContext.js aceita
 * X-Batch-Id de qualquer rota; a lista cresce sem exigir mudança aqui). */
export function describeBatchLabel(action, count) {
  const plural = count === 1 ? 'cartão' : 'cartões';
  switch (action) {
    case 'card.moved': return `moveu ${count} ${plural}`;
    case 'card.status.changed': return `alterou o status de ${count} ${plural}`;
    case 'card.archived': return `arquivou ${count} ${plural}`;
    case 'card.restored': return `restaurou ${count} ${plural}`;
    case 'card.deleted': return `excluiu ${count} ${plural}`;
    default: return `${count} eventos em lote`;
  }
}

// --- exportação CSV ---------------------------------------------------------

export const EXPORT_STATUS_SENTINEL = '__EXPORT_STATUS__';

/** Parser mínimo de UMA linha CSV (RFC 4180: aspas sempre presentes, "" é
 * aspas literal) — suficiente para ler a linha de status; não é um parser de
 * arquivo inteiro, e não precisa ser (csvRow/csvStatusRow — events.routes.js). */
export function parseCsvLine(line) {
  const cells = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; } else inQuotes = false;
      } else {
        cur += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      cells.push(cur);
      cur = '';
    } else {
      cur += c;
    }
  }
  cells.push(cur);
  return cells;
}

/**
 * Lê a última linha do CSV baixado e devolve o desfecho da exportação, ou
 * null se a linha de status não está lá (arquivo cortado no transporte —
 * ver o comentário de csvStatusRow em events.routes.js).
 */
export function parseExportStatus(csvText) {
  const lines = csvText.split('\r\n').filter((l) => l.length > 0);
  if (!lines.length) return null;
  const last = parseCsvLine(lines[lines.length - 1]);
  if (last[0] !== EXPORT_STATUS_SENTINEL) return null;
  return { outcome: last[1] || '', reason: last[2] || '', count: Number(last[3]) || 0 };
}

/** Espelha os motivos que events.routes.js grava (OUTCOME_* / reason). */
export const EXPORT_REASON_LABEL = {
  limite_de_paginas: 'limite de segurança de páginas foi atingido',
  autorizacao_revogada: 'seu acesso a este recorte foi revogado durante a exportação',
  erro: 'ocorreu um erro interno durante a exportação',
  cliente_desconectou: 'a conexão foi interrompida antes do fim',
};

/** Nome do arquivo a partir do header Content-Disposition, com fallback. */
export function filenameFromContentDisposition(header, fallback = 'events.csv') {
  const m = /filename="([^"]+)"/.exec(header || '');
  return m ? m[1] : fallback;
}
